// Runtime-independent feature metadata shared by the customer app and local operator portal.
export const Flags = {
    TeamChatModule: "TEAM_CHAT_MODULE",
    MeatModule: "MEAT_MODULE",
    IngredientsModule: "INGREDIENTS_MODULE",
    PackagingModule: "PACKAGING_MODULE",
    StockPricing: "STOCK_PRICING",
    VolumeIngredients: "VOLUME_INGREDIENTS",
    DeliveriesModule: "DELIVERIES_MODULE",
    StockModule: "STOCK_MODULE",
    WastageModule: "WASTAGE_MODULE",
    RecipesModule: "RECIPES_MODULE",
    ProductsModule: "PRODUCTS_MODULE",
    DispatchModule: "DISPATCH_MODULE",
    DepartmentsModule: "DEPARTMENTS_MODULE",
    TemperatureLogging: "TEMPERATURE_LOGGING_MODULE",
    SuppliersModule: "SUPPLIERS_MODULE",
    // Covers checks, the check form builder and the shared form library.
    ChecksModule: "CHECKS_MODULE",
    UserClockModule: "USERCLOCK_MODULE",
    WagesModule: "WAGES_MODULE",
    FilesModule: "FILES_MODULE",
    DadJokes: "DAD_JOKES",
    AllergensModule: "ALLERGENS_MODULE",
    MenusModule: "MENUS_MODULE",
    PublicQRMenus: "PUBLIC_QR_MENUS",
    FoodRecords: "FOOD_RECORDS",
    KioskMode: "KIOSK_MODE",
    EasyUserSwitch: "EASY_USER_SWITCH",
    SensorModule: "SENSOR_MODULE",
    AIModule: "AI_MODULE",
    AuditModule: "AUDIT_MODULE",
    IssuesModule: "ISSUES_MODULE",
    HACCPModule: "HACCP_MODULE",
    ComplaintsModule: "COMPLAINTS_MODULE",
    TrainingModule: "TRAINING_MODULE",
    RotaModule: "ROTA_MODULE",
    MultiRecipeProduction: "MULTI_RECIPE_PRODUCTION",
    LabelTemplates: "LABEL_TEMPLATES_MODULE",
    IngredientImport: "INGREDIENT_IMPORT",
    RackProduction: "RACK_PRODUCTION_MODULE",
    // When set, a production run may consume from at most one intake per stock:
    // the allocator stops auto-spilling into other batches and blocks the run
    // with a warning instead. For tenants who only ever produce from a single
    // batch and don't want a short batch silently topped up from others.
    SingleIntakeMode: "SINGLE_INTAKE_MODE",
    // Operator-only display preference: hide answer attribution and record creation time.
    // Metadata stays in stored checks; the performed time remains visible.
    HideCheckAnswerAttribution: "HIDE_CHECK_ANSWER_ATTRIBUTION"
};

export const featureCatalogGroups = [
    {
        id: "production-traceability",
        title: "Production & Traceability",
        description: "Intake, recipe, product, stock and dispatch workflows.",
        features: [
            {
                flag: Flags.MeatModule,
                name: "Meat Intakes",
                icon: "fa-drumstick-bite",
                description:
                    "Record meat deliveries with batch, supplier, origin, use-by and quality check details.",
                tags: ["Intake", "Traceability"]
            },
            {
                flag: Flags.IngredientsModule,
                name: "Ingredient Intakes",
                icon: "fa-carrot",
                description:
                    "Track ingredient batches from supplier intake through stock, recipes and production.",
                tags: ["Intake", "Traceability"]
            },
            {
                flag: Flags.IngredientImport,
                name: "Ingredient Import",
                icon: "fa-file-import",
                description:
                    "Bulk-import ingredient lists from a spreadsheet, matching rows to existing stock and creating lots.",
                tags: ["Import", "Ingredients"]
            },
            {
                flag: Flags.PackagingModule,
                name: "Packaging Intakes",
                icon: "fa-box-open",
                description:
                    "Log packaging batches, supplier details and intake checks for packaging traceability.",
                tags: ["Intake", "Packaging"]
            },
            {
                flag: Flags.DeliveriesModule,
                name: "Deliveries",
                icon: "fa-truck-loading",
                description:
                    "Group received meat, ingredient and packaging lines into delivery records.",
                tags: ["Intake", "Suppliers"]
            },
            {
                flag: Flags.StockModule,
                name: "Stock",
                icon: "fa-boxes",
                description:
                    "Manage stock quantities, batch status, bookings and inventory movements.",
                tags: ["Inventory", "Traceability"]
            },
            {
                flag: Flags.VolumeIngredients,
                name: "Volume ingredients",
                icon: "fa-tint",
                description: "Measure ingredient intake and recipe usage in litres or millilitres.",
                tags: ["Inventory", "Recipes"]
            },
            {
                flag: Flags.StockPricing,
                name: "Stock Pricing",
                icon: "fa-tags",
                description:
                    "Capture unit costs and stock values on intakes, stock records and product costing.",
                tags: ["Costs", "Inventory"]
            },
            {
                flag: Flags.RecipesModule,
                name: "Recipes",
                icon: "fa-hat-chef",
                description:
                    "Build recipes with ingredient quantities, allergens, methods and batch sheets.",
                tags: ["Recipes", "Production"]
            },
            {
                flag: Flags.ProductsModule,
                name: "Products",
                icon: "fa-burger-soda",
                description:
                    "Create finished products, generate traceability records and connect production to labels.",
                tags: ["Products", "Traceability"]
            },
            {
                flag: Flags.LabelTemplates,
                name: "Label Templates",
                icon: "fa-tag",
                description:
                    "Design and manage reusable HTML label templates for product and packaging printing.",
                tags: ["Labels", "Products"]
            },
            {
                flag: Flags.MultiRecipeProduction,
                name: "Production Runs",
                icon: "fa-industry",
                description:
                    "Plan and complete multi-recipe production runs with allocations and run records.",
                tags: ["Production", "Planning"]
            },
            {
                flag: Flags.RackProduction,
                name: "Rack Production",
                icon: "fa-layer-group",
                description:
                    "Track multi-stage in-house production through rack and count stages between intake and finished product.",
                tags: ["Production", "Planning"]
            },
            {
                flag: Flags.SingleIntakeMode,
                name: "Single-Intake Production",
                icon: "fa-box",
                description:
                    "Restrict each production run to one intake batch per stock — no silent topping up from other batches.",
                tags: ["Production", "Traceability"]
            },
            {
                flag: Flags.DispatchModule,
                name: "Dispatches",
                icon: "fa-shipping-fast",
                description:
                    "Record customer dispatches and connect outbound product movement to traceability.",
                tags: ["Dispatch", "Customers"]
            },
            {
                flag: Flags.WastageModule,
                name: "Wastage",
                icon: "fa-trash-alt",
                description:
                    "Log wasted stock or products with reasons, values and supporting notes.",
                tags: ["Stock", "Losses"]
            },
            {
                flag: Flags.SuppliersModule,
                name: "Suppliers",
                icon: "fa-industry-alt",
                description:
                    "Maintain supplier records, contact details and supporting supplier documents.",
                tags: ["Suppliers", "Records"]
            },
            {
                flag: Flags.AllergensModule,
                name: "Allergens",
                icon: "fa-exclamation-circle",
                description:
                    "Manage menu items and allergen matrices for customer-facing allergen information.",
                tags: ["Compliance", "Menus"]
            },
            {
                flag: Flags.MenusModule,
                name: "Menus",
                icon: "fa-book-open",
                description:
                    "Build typed menus (set lunch, à la carte…) from categories of dishes, manually or from recipes, with allergen tracking per item.",
                tags: ["Compliance", "Menus", "Hospitality"]
            },
            {
                flag: Flags.PublicQRMenus,
                name: "Public QR Menus",
                icon: "fa-qrcode",
                description:
                    "Publish branded, location-specific menus that diners can open by QR code and filter by allergens.",
                tags: ["Menus", "Allergens", "Customer experience"]
            }
        ]
    },
    {
        id: "food-safety-records",
        title: "Food Safety Records",
        description: "Compliance records, checks, plans, incidents and documents.",
        features: [
            {
                flag: Flags.TemperatureLogging,
                name: "Temperature logging",
                icon: "fa-temperature-high",
                description:
                    "Capture fridge, freezer, cooking and holding temperatures across locations.",
                tags: ["Records", "Temperatures"]
            },
            {
                flag: Flags.FoodRecords,
                name: "Food Temperature Records",
                icon: "fa-thermometer",
                description:
                    "Record food-specific temperature checks with item, location and corrective action details.",
                tags: ["Records", "Temperatures"]
            },
            {
                flag: Flags.ChecksModule,
                name: "Checks",
                icon: "fa-clipboard-check",
                description:
                    "Schedule and complete routine operational and food safety checks, design custom check forms, and adopt ready-made forms from the shared library.",
                tags: ["Checks", "Compliance", "Forms"]
            },
            {
                flag: Flags.HACCPModule,
                name: "HACCP Plans",
                icon: "fa-clipboard-list",
                description:
                    "Build, review and publish HACCP plans with hazards, controls and revision records.",
                tags: ["HACCP", "Compliance"]
            },
            {
                flag: Flags.AuditModule,
                name: "Audits",
                icon: "fa-search",
                description:
                    "Run audit records, findings and corrective actions for internal or external reviews.",
                tags: ["Audits", "Compliance"]
            },
            {
                flag: Flags.IssuesModule,
                name: "Issues",
                icon: "fa-exclamation-triangle",
                description:
                    "Raise, assign and resolve issues from checks, temperatures, intakes and operations.",
                tags: ["Corrective actions", "Workflow"]
            },
            {
                flag: Flags.ComplaintsModule,
                name: "Complaints",
                icon: "fa-comment-exclamation",
                description:
                    "Track customer and supplier complaints with history, evidence and follow-up actions.",
                tags: ["Complaints", "Quality"]
            },
            {
                flag: Flags.SensorModule,
                name: "Sensor monitoring",
                icon: "fa-monitor-heart-rate",
                description: "Connect temperature sensors to monitor readings and location status.",
                tags: ["Sensors", "Monitoring"]
            },
            {
                flag: Flags.FilesModule,
                name: "Files",
                icon: "fa-folders",
                description:
                    "Store and organise certificates, photos, reports and other business documents.",
                tags: ["Documents", "Storage"]
            },
            {
                flag: Flags.TrainingModule,
                name: "Training Matrix",
                icon: "fa-graduation-cap",
                description:
                    "Track staff training completion, expiry dates and supporting notes in one matrix.",
                tags: ["Training", "Staff", "Compliance", "Renewals"]
            }
        ]
    },
    {
        id: "workforce-operations",
        title: "Workforce & Operations",
        description: "People, departments, rota, reporting and operational tools.",
        features: [
            {
                flag: Flags.TeamChatModule,
                name: "Team chat",
                icon: "fa-comments",
                description:
                    "Send text messages and mention colleagues in team, location and department groups.",
                tags: ["Communication", "Teams"]
            },
            {
                flag: Flags.DepartmentsModule,
                name: "Departments",
                icon: "fa-sitemap",
                description:
                    "Group users, files and records by department for larger or multi-area teams.",
                tags: ["Organisation", "Access"]
            },
            {
                flag: Flags.RotaModule,
                name: "Staff Rota",
                icon: "fa-calendar-week",
                description: "Plan team shifts and give users their own schedule view.",
                tags: ["Rota", "People"]
            },
            {
                flag: Flags.UserClockModule,
                name: "Clock In / Out",
                icon: "fa-user-clock",
                description:
                    "Let users clock in and out, then review attendance events and hours worked.",
                tags: ["Time", "People"]
            },
            {
                flag: Flags.WagesModule,
                name: "Payroll Reports",
                icon: "fa-money-check-alt",
                description: "Add wage-rate details and payroll-style reporting for clock events.",
                tags: ["Payroll", "Reports"]
            }
        ]
    },
    {
        id: "access-experience",
        title: "Access & Experience",
        description: "Tenant-specific access controls and lightweight experience options.",
        features: [
            {
                flag: Flags.KioskMode,
                name: "Kiosk Mode",
                icon: "fa-tablet-alt",
                description: "Keep shared-device sessions focused on operational entry screens.",
                tags: ["Device", "Access"]
            },
            {
                flag: Flags.EasyUserSwitch,
                name: "Easy User Switch",
                icon: "fa-users-cog",
                description: "Allow faster switching between users on shared devices.",
                tags: ["Device", "Users"]
            },
            {
                flag: Flags.DadJokes,
                name: "Dashboard Jokes",
                icon: "fa-laugh",
                description: "Show a light dashboard joke widget for teams that want it.",
                tags: ["Dashboard", "Optional"]
            }
        ]
    }
];

export const customerHiddenFeatureFlags = [Flags.AIModule, Flags.HideCheckAnswerAttribution];

export const featureBundles = [
    {
        id: "factory-production",
        title: "Factory Production",
        icon: "fa-industry",
        description: "Run recipes, consume stock and create traceable finished products.",
        required: [
            Flags.StockModule,
            Flags.RecipesModule,
            Flags.ProductsModule,
            Flags.MultiRecipeProduction
        ],
        choiceGroups: [
            {
                id: "intake-lanes",
                title: "Material intake lane",
                description: "Enable at least one material intake lane.",
                min: 1,
                flags: [Flags.MeatModule, Flags.IngredientsModule, Flags.PackagingModule]
            }
        ],
        recommended: [Flags.SuppliersModule],
        optional: [
            Flags.DeliveriesModule,
            Flags.DispatchModule,
            Flags.WastageModule,
            Flags.StockPricing,
            Flags.AllergensModule
        ]
    },
    {
        id: "digital-menus",
        title: "Digital Menus",
        icon: "fa-qrcode",
        description: "Build allergen-aware menus and publish branded QR menus for customers.",
        required: [Flags.MenusModule, Flags.PublicQRMenus],
        recommended: [Flags.AllergensModule],
        optional: []
    },
    {
        id: "food-safety-compliance",
        title: "Food Safety & Compliance",
        icon: "fa-clipboard-check",
        description: "Record checks, temperatures, HACCP evidence and corrective actions.",
        required: [
            Flags.ChecksModule,
            Flags.TemperatureLogging,
            Flags.HACCPModule,
            Flags.IssuesModule
        ],
        recommended: [
            Flags.FoodRecords,
            Flags.AuditModule,
            Flags.ComplaintsModule,
            Flags.FilesModule,
            Flags.TrainingModule
        ],
        optional: []
    },
    {
        id: "people-workforce",
        title: "People & Workforce",
        icon: "fa-users",
        description: "Plan shifts, capture attendance and report payroll information.",
        required: [Flags.RotaModule, Flags.UserClockModule],
        recommended: [Flags.WagesModule, Flags.DepartmentsModule],
        optional: [Flags.TrainingModule, Flags.KioskMode, Flags.EasyUserSwitch]
    },
    {
        id: "monitoring-reporting",
        title: "Monitoring Add-ons",
        icon: "fa-chart-line",
        description: "Extend core reporting with live sensor monitoring.",
        choiceGroups: [
            {
                id: "monitoring-addon",
                title: "Monitoring add-on",
                description: "Enable sensor monitoring.",
                min: 1,
                flags: [Flags.SensorModule]
            }
        ],
        optional: []
    },
    {
        id: "documents-outputs",
        title: "Documents & Customer Outputs",
        icon: "fa-file-pdf",
        description: "Store documents and generate forms, labels and trace outputs.",
        required: [Flags.FilesModule],
        recommended: [Flags.ComplaintsModule, Flags.HACCPModule],
        optional: []
    },
    {
        id: "retail-lightweight-tools",
        title: "Lightweight Tools",
        icon: "fa-calculator",
        description: "Add shared-device helpers and optional experience tools for specific teams.",
        choiceGroups: [
            {
                id: "lightweight-tool",
                title: "Lightweight tool",
                description: "Enable at least one lightweight tool.",
                min: 1,
                flags: [Flags.KioskMode, Flags.EasyUserSwitch, Flags.DadJokes]
            }
        ],
        optional: []
    }
];
